import { useState, useEffect } from "react";
import {
  X,
  Truck,
  Building2,
  MapPin,
  Phone,
  User,
  UserCheck,
  Calendar,
  AlertCircle,
  Package,
  CheckCircle2,
  Layers,
  PlusCircle,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { errorMessage, getTodayDateString } from "../../lib/format";

export default function SendDirectDeliveryModal({
  isOpen,
  onClose,
  orders = [],
  client,
  currentUser,
  onSuccess,
}) {
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [requiredDate, setRequiredDate] = useState("");
  const [deliveryRemarks, setDeliveryRemarks] = useState("");

  // Pickup location state
  const [pickupType, setPickupType] = useState("Warehouse"); // "Warehouse" | "Custom"
  const [warehouses, setWarehouses] = useState([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [selectedWarehouseId, setSelectedWarehouseId] = useState("");

  // Custom pickup state
  const [customLocationName, setCustomLocationName] = useState("");
  const [customPickupAddress, setCustomPickupAddress] = useState("");
  const [customContactPerson, setCustomContactPerson] = useState("");
  const [customContactPhone, setCustomContactPhone] = useState("");

  // Delivery Person state
  const [deliveryAgents, setDeliveryAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState("");

  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Initialize form fields from client and orders
  useEffect(() => {
    if (!isOpen) return;

    // Delivery address: auto-populate from client shipping or billing address or order delivery address
    const initialAddress =
      client?.shipping_address ||
      client?.billing_address ||
      orders[0]?.delivery_address ||
      "";
    setDeliveryAddress(initialAddress);

    // Contact details
    const initialContact =
      client?.contact_person ||
      orders[0]?.client_contact_person ||
      client?.company_name ||
      "";
    setContactPerson(initialContact);

    const initialPhone =
      client?.phone || orders[0]?.client_phone || "";
    setContactPhone(initialPhone);

    // Delivery deadline
    const initialDate =
      orders[0]?.required_delivery_date || getTodayDateString();
    setRequiredDate(initialDate);

    setDeliveryRemarks("");
    setFormErrors({});
    setSubmitting(false);

    // Load active warehouses
    setLoadingWarehouses(true);
    api("get", "/warehouses?status=Active")
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setWarehouses(list);
        if (list.length > 0) {
          setSelectedWarehouseId(list[0].warehouse_id);
          setPickupType("Warehouse");
        } else {
          setPickupType("Custom");
        }
      })
      .catch((err) => {
        console.error("Failed to load warehouses:", err);
        setWarehouses([]);
        setPickupType("Custom");
      })
      .finally(() => setLoadingWarehouses(false));

    // Load active delivery personnel
    setLoadingAgents(true);
    api("get", "/users?department=Delivery&active_only=true")
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        const validAgents = list.filter(
          (u) =>
            !u.is_deleted &&
            u.is_active !== false &&
            ((u.department || "").toLowerCase() === "delivery" ||
              (u.role || "").toLowerCase() === "delivery")
        );
        setDeliveryAgents(validAgents);
        if (validAgents.length > 0) {
          setSelectedAgentId(validAgents[0].user_id || validAgents[0].name);
        } else {
          setSelectedAgentId("");
        }
      })
      .catch((err) => {
        console.error("Failed to load delivery personnel:", err);
        setDeliveryAgents([]);
        setSelectedAgentId("");
      })
      .finally(() => setLoadingAgents(false));
  }, [isOpen, client, orders]);

  if (!isOpen || !orders || orders.length === 0) return null;

  const selectedWarehouse = warehouses.find(
    (w) => w.warehouse_id === selectedWarehouseId
  );

  const selectedAgent = deliveryAgents.find(
    (a) => (a.user_id && a.user_id === selectedAgentId) || a.name === selectedAgentId
  );

  const validateForm = () => {
    const errors = {};

    if (!deliveryAddress || !deliveryAddress.trim()) {
      errors.deliveryAddress = "Delivery address is required.";
    }

    if (!contactPerson || !contactPerson.trim()) {
      errors.contactPerson = "Recipient contact person is required.";
    }

    if (!contactPhone || !contactPhone.trim()) {
      errors.contactPhone = "Recipient contact phone number is required.";
    }

    if (!requiredDate || !requiredDate.trim()) {
      errors.requiredDate = "Delivery deadline date is required.";
    }

    if (pickupType === "Warehouse") {
      if (!selectedWarehouseId) {
        errors.warehouse = "Please select a warehouse location.";
      }
    } else {
      if (!customPickupAddress || !customPickupAddress.trim()) {
        errors.customPickupAddress = "Pickup address is required for custom pickup location.";
      }
      if (!customContactPerson || !customContactPerson.trim()) {
        errors.customContactPerson = "Contact person at pickup location is required.";
      }
      if (!customContactPhone || !customContactPhone.trim()) {
        errors.customContactPhone = "Contact phone number at pickup location is required.";
      }
    }

    // Delivery person validation
    if (!selectedAgentId || !selectedAgent) {
      errors.deliveryAgent = "Please select an assigned delivery person.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fill in all mandatory fields including assigned delivery person.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        order_ids: orders.map((o) => o.order_id),
        delivery_address: deliveryAddress.trim(),
        client_contact_person: contactPerson.trim(),
        client_phone: contactPhone.trim(),
        required_delivery_date: requiredDate.trim(),
        pickup_type: pickupType,
        warehouse_id: pickupType === "Warehouse" ? selectedWarehouseId : "",
        warehouse_name:
          pickupType === "Warehouse"
            ? selectedWarehouse?.name || selectedWarehouse?.warehouse_name || ""
            : customLocationName.trim() || "Custom Pickup Location",
        pickup_location_name:
          pickupType === "Warehouse"
            ? selectedWarehouse?.name || selectedWarehouse?.warehouse_name || ""
            : customLocationName.trim() || "Custom Pickup Location",
        pickup_address:
          pickupType === "Warehouse"
            ? selectedWarehouse?.address || selectedWarehouse?.location || ""
            : customPickupAddress.trim(),
        pickup_contact_person:
          pickupType === "Warehouse"
            ? selectedWarehouse?.contact_person || ""
            : customContactPerson.trim(),
        pickup_phone:
          pickupType === "Warehouse"
            ? selectedWarehouse?.phone || ""
            : customContactPhone.trim(),
        assigned_delivery_person: selectedAgent ? selectedAgent.name : "",
        delivery_person_id: selectedAgent ? (selectedAgent.user_id || "") : "",
        delivery_person_phone: selectedAgent ? (selectedAgent.phone || "") : "",
        remarks: deliveryRemarks.trim() || "Direct delivery from Client Details",
      };

      const res = await api("post", "/orders/direct-delivery", payload);
      const count = res.data?.updated_count || orders.length;

      toast.success(
        `Successfully sent ${count} order${count === 1 ? "" : "s"} to Delivery (Assigned: ${selectedAgent.name})!`
      );

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error("Direct delivery submission error:", err);
      toast.error(errorMessage(err, "Failed to send order(s) to Delivery"));
    } finally {
      setSubmitting(false);
    }
  };

  const isBulk = orders.length > 1;

  return (
    <div
      className="modal-backdrop"
      style={{ zIndex: 10002 }}
      data-testid="direct-delivery-modal-backdrop"
    >
      <div
        className="modal"
        style={{
          maxWidth: 680,
          width: "95%",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          borderRadius: 12,
          padding: 0,
          overflow: "hidden",
        }}
        data-testid="direct-delivery-modal"
      >
        {/* Modal Header */}
        <div
          style={{
            padding: "18px 24px",
            borderBottom: "1px solid var(--line)",
            background: "linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)",
            color: "#ffffff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 8,
                background: "rgba(124, 58, 237, 0.25)",
                border: "1px solid rgba(167, 139, 250, 0.4)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#c4b5fd",
              }}
            >
              <Truck size={20} />
            </div>
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                  color: "#a78bfa",
                  textTransform: "uppercase",
                }}
              >
                DIRECT DISPATCH & DELIVERY
              </div>
              <h2 style={{ margin: 0, fontSize: 17, color: "#ffffff", fontWeight: 700 }}>
                {isBulk
                  ? `Send ${orders.length} Orders to Delivery`
                  : `Send Order #${orders[0]?.order_id} to Delivery`}
              </h2>
            </div>
          </div>
          <button
            type="button"
            className="icon-btn"
            onClick={() => !submitting && onClose()}
            disabled={submitting}
            data-testid="direct-delivery-close-btn"
            style={{ color: "#94a3b8", background: "rgba(255, 255, 255, 0.08)" }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Modal Body */}
        <form
          onSubmit={handleSubmit}
          style={{
            padding: "20px 24px",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 20,
          }}
        >
          {/* Order Summary Strip */}
          <div
            style={{
              padding: "12px 16px",
              background: "#faf5ff",
              border: "1px solid #e9d5ff",
              borderRadius: 8,
              fontSize: 13,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: isBulk ? 8 : 0,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    padding: "2px 8px",
                    borderRadius: 4,
                    background: "#7c3aed",
                    color: "#ffffff",
                  }}
                >
                  {isBulk ? `${orders.length} Selected Orders` : "Single Order"}
                </span>
                <span style={{ fontWeight: 600, color: "#0f172a" }}>
                  Client: {client?.company_name || orders[0]?.client_name}
                </span>
              </div>
              <span style={{ fontSize: 11.5, color: "#6b21a8", fontWeight: 600 }}>
                Bypasses Design & Production
              </span>
            </div>

            {/* List selected orders */}
            {isBulk ? (
              <div
                style={{
                  maxHeight: 110,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                  marginTop: 6,
                  paddingRight: 4,
                }}
              >
                {orders.map((o) => (
                  <div
                    key={o.order_id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 12,
                      padding: "4px 8px",
                      background: "#ffffff",
                      borderRadius: 4,
                      border: "1px solid #f3e8ff",
                    }}
                  >
                    <span style={{ fontFamily: "monospace", fontWeight: 600, color: "#581c87" }}>
                      #{o.order_id}
                    </span>
                    <span style={{ color: "#334155", fontWeight: 500 }}>{o.product}</span>
                    <span style={{ color: "#64748b" }}>
                      {(o.quantity || 1).toLocaleString()} units
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ marginTop: 6, fontSize: 12.5, color: "#475569" }}>
                <b>{orders[0]?.product}</b> · {(orders[0]?.quantity || 1).toLocaleString()} units
              </div>
            )}
          </div>

          {/* SECTION 1: Delivery Destination Details */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 13,
                fontWeight: 700,
                color: "#0f172a",
                marginBottom: 12,
                textTransform: "uppercase",
                letterSpacing: "0.3px",
              }}
            >
              <MapPin size={15} color="#7c3aed" />
              1. Delivery Destination & Recipient
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div>
                <label className="dispatch-label" style={{ fontSize: 12, marginBottom: 4 }}>
                  Recipient Contact Person <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={contactPerson}
                  onChange={(e) => setContactPerson(e.target.value)}
                  placeholder="e.g. John Doe / Karan Malhotra"
                  data-testid="direct-delivery-contact-person"
                  style={{
                    borderColor: formErrors.contactPerson ? "#ef4444" : undefined,
                  }}
                />
                {formErrors.contactPerson && (
                  <span style={{ color: "#dc2626", fontSize: 11, marginTop: 2, display: "block" }}>
                    {formErrors.contactPerson}
                  </span>
                )}
              </div>

              <div>
                <label className="dispatch-label" style={{ fontSize: 12, marginBottom: 4 }}>
                  Recipient Phone Number <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <input
                  type="tel"
                  className="form-control"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder="e.g. +91 98765 43210"
                  data-testid="direct-delivery-contact-phone"
                  style={{
                    borderColor: formErrors.contactPhone ? "#ef4444" : undefined,
                  }}
                />
                {formErrors.contactPhone && (
                  <span style={{ color: "#dc2626", fontSize: 11, marginTop: 2, display: "block" }}>
                    {formErrors.contactPhone}
                  </span>
                )}
              </div>
            </div>

            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <label className="dispatch-label" style={{ fontSize: 12, margin: 0 }}>
                  Delivery / Shipping Address <span style={{ color: "#dc2626" }}>*</span>
                </label>
                {(client?.shipping_address || client?.billing_address) && (
                  <span style={{ fontSize: 11, color: "#16a34a", fontWeight: 500 }}>
                    ✓ Auto-populated from client profile
                  </span>
                )}
              </div>
              <textarea
                className="form-control"
                rows={2}
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
                placeholder="Enter complete delivery address with landmark and pincode..."
                data-testid="direct-delivery-address-input"
                style={{
                  resize: "vertical",
                  borderColor: formErrors.deliveryAddress ? "#ef4444" : undefined,
                }}
              />
              {formErrors.deliveryAddress && (
                <span style={{ color: "#dc2626", fontSize: 11, marginTop: 2, display: "block" }}>
                  {formErrors.deliveryAddress}
                </span>
              )}
            </div>

            <div style={{ marginTop: 12 }}>
              <label className="dispatch-label" style={{ fontSize: 12, marginBottom: 4 }}>
                Delivery Deadline Date <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <input
                type="date"
                className="form-control"
                value={requiredDate}
                onChange={(e) => setRequiredDate(e.target.value)}
                data-testid="direct-delivery-date-input"
                style={{
                  maxWidth: 240,
                  borderColor: formErrors.requiredDate ? "#ef4444" : undefined,
                }}
              />
              {formErrors.requiredDate && (
                <span style={{ color: "#dc2626", fontSize: 11, marginTop: 2, display: "block" }}>
                  {formErrors.requiredDate}
                </span>
              )}
            </div>
          </div>

          <div style={{ borderTop: "1px solid var(--line)" }} />

          {/* SECTION 2: Pickup Location */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 13,
                  fontWeight: 700,
                  color: "#0f172a",
                  textTransform: "uppercase",
                  letterSpacing: "0.3px",
                }}
              >
                <Building2 size={15} color="#7c3aed" />
                2. Pickup Origin Location
              </div>

              {/* Toggle Warehouse vs Custom */}
              <div
                style={{
                  display: "flex",
                  background: "#f1f5f9",
                  padding: 2,
                  borderRadius: 6,
                  gap: 2,
                }}
              >
                <button
                  type="button"
                  onClick={() => setPickupType("Warehouse")}
                  data-testid="pickup-type-warehouse"
                  style={{
                    border: "none",
                    padding: "4px 10px",
                    borderRadius: 5,
                    fontSize: 11.5,
                    fontWeight: 600,
                    cursor: "pointer",
                    background: pickupType === "Warehouse" ? "#ffffff" : "transparent",
                    color: pickupType === "Warehouse" ? "#7c3aed" : "#64748b",
                    boxShadow:
                      pickupType === "Warehouse" ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
                  }}
                >
                  🏢 Existing Warehouse
                </button>
                <button
                  type="button"
                  onClick={() => setPickupType("Custom")}
                  data-testid="pickup-type-custom"
                  style={{
                    border: "none",
                    padding: "4px 10px",
                    borderRadius: 5,
                    fontSize: 11.5,
                    fontWeight: 600,
                    cursor: "pointer",
                    background: pickupType === "Custom" ? "#ffffff" : "transparent",
                    color: pickupType === "Custom" ? "#7c3aed" : "#64748b",
                    boxShadow: pickupType === "Custom" ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
                  }}
                >
                  📍 Add New Pickup Address
                </button>
              </div>
            </div>

            {/* Warehouse Mode */}
            {pickupType === "Warehouse" && (
              <div>
                <label className="dispatch-label" style={{ fontSize: 12, marginBottom: 4 }}>
                  Select Warehouse Location <span style={{ color: "#dc2626" }}>*</span>
                </label>
                {loadingWarehouses ? (
                  <div style={{ fontSize: 12, color: "var(--muted)", padding: 8 }}>
                    Loading active warehouses…
                  </div>
                ) : warehouses.length === 0 ? (
                  <div
                    style={{
                      padding: "10px 14px",
                      background: "#fffbeb",
                      border: "1px solid #fef3c7",
                      borderRadius: 6,
                      fontSize: 12,
                      color: "#92400e",
                    }}
                  >
                    No saved warehouses found. Please switch to <b>"Add New Pickup Address"</b> to specify pickup origin.
                  </div>
                ) : (
                  <select
                    className="form-control"
                    value={selectedWarehouseId}
                    onChange={(e) => setSelectedWarehouseId(e.target.value)}
                    data-testid="direct-delivery-warehouse-select"
                    style={{
                      borderColor: formErrors.warehouse ? "#ef4444" : undefined,
                    }}
                  >
                    {warehouses.map((w) => (
                      <option key={w.warehouse_id} value={w.warehouse_id}>
                        {w.name || w.warehouse_name} ({w.location || w.address || "Warehouse"})
                      </option>
                    ))}
                  </select>
                )}
                {formErrors.warehouse && (
                  <span style={{ color: "#dc2626", fontSize: 11, marginTop: 2, display: "block" }}>
                    {formErrors.warehouse}
                  </span>
                )}

                {/* Selected Warehouse Live Card */}
                {selectedWarehouse && (
                  <div
                    style={{
                      marginTop: 10,
                      padding: "10px 14px",
                      background: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      borderRadius: 6,
                      fontSize: 12,
                    }}
                  >
                    <div style={{ fontWeight: 600, color: "#0f172a", marginBottom: 2 }}>
                      {selectedWarehouse.name || selectedWarehouse.warehouse_name}
                    </div>
                    <div style={{ color: "#475569", lineHeight: 1.4 }}>
                      📍 {selectedWarehouse.address || selectedWarehouse.location || "Logistics Hub"}
                    </div>
                    {(selectedWarehouse.contact_person || selectedWarehouse.phone) && (
                      <div style={{ color: "#64748b", fontSize: 11.5, marginTop: 4 }}>
                        Contact: {selectedWarehouse.contact_person || "Warehouse Supervisor"} ·{" "}
                        {selectedWarehouse.phone || "—"}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Custom Pickup Address Mode */}
            {pickupType === "Custom" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div>
                  <label className="dispatch-label" style={{ fontSize: 12, marginBottom: 4 }}>
                    Pickup Location Name / Tag (Optional)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={customLocationName}
                    onChange={(e) => setCustomLocationName(e.target.value)}
                    placeholder="e.g. Supplier Facility, Okhla Factory Unit, Branch Warehouse"
                    data-testid="direct-delivery-custom-location-name"
                  />
                </div>

                <div>
                  <label className="dispatch-label" style={{ fontSize: 12, marginBottom: 4 }}>
                    Pickup Address <span style={{ color: "#dc2626" }}>*</span>
                  </label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={customPickupAddress}
                    onChange={(e) => setCustomPickupAddress(e.target.value)}
                    placeholder="Enter full pickup address where goods are ready..."
                    data-testid="direct-delivery-custom-pickup-address"
                    style={{
                      resize: "vertical",
                      borderColor: formErrors.customPickupAddress ? "#ef4444" : undefined,
                    }}
                  />
                  {formErrors.customPickupAddress && (
                    <span style={{ color: "#dc2626", fontSize: 11, marginTop: 2, display: "block" }}>
                      {formErrors.customPickupAddress}
                    </span>
                  )}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div>
                    <label className="dispatch-label" style={{ fontSize: 12, marginBottom: 4 }}>
                      Contact Person at Pickup <span style={{ color: "#dc2626" }}>*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={customContactPerson}
                      onChange={(e) => setCustomContactPerson(e.target.value)}
                      placeholder="e.g. Ramesh Chandra"
                      data-testid="direct-delivery-custom-contact-person"
                      style={{
                        borderColor: formErrors.customContactPerson ? "#ef4444" : undefined,
                      }}
                    />
                    {formErrors.customContactPerson && (
                      <span style={{ color: "#dc2626", fontSize: 11, marginTop: 2, display: "block" }}>
                        {formErrors.customContactPerson}
                      </span>
                    )}
                  </div>

                  <div>
                    <label className="dispatch-label" style={{ fontSize: 12, marginBottom: 4 }}>
                      Contact Phone at Pickup <span style={{ color: "#dc2626" }}>*</span>
                    </label>
                    <input
                      type="tel"
                      className="form-control"
                      value={customContactPhone}
                      onChange={(e) => setCustomContactPhone(e.target.value)}
                      placeholder="e.g. +91 98111 22334"
                      data-testid="direct-delivery-custom-contact-phone"
                      style={{
                        borderColor: formErrors.customContactPhone ? "#ef4444" : undefined,
                      }}
                    />
                    {formErrors.customContactPhone && (
                      <span style={{ color: "#dc2626", fontSize: 11, marginTop: 2, display: "block" }}>
                        {formErrors.customContactPhone}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div style={{ borderTop: "1px solid var(--line)" }} />

          {/* SECTION 3: Assigned Delivery Person (Mandatory) */}
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 13,
                fontWeight: 700,
                color: "#0f172a",
                marginBottom: 12,
                textTransform: "uppercase",
                letterSpacing: "0.3px",
              }}
            >
              <UserCheck size={15} color="#7c3aed" />
              3. Assigned Delivery Person <span style={{ color: "#dc2626" }}>*</span>
            </div>

            {loadingAgents ? (
              <div style={{ fontSize: 12, color: "var(--muted)", padding: 8 }}>
                Loading active delivery staff…
              </div>
            ) : deliveryAgents.length === 0 ? (
              <div
                style={{
                  padding: "12px 14px",
                  background: "#fef2f2",
                  border: "1.5px solid #fecaca",
                  borderRadius: 6,
                  fontSize: 12.5,
                  color: "#991b1b",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
                data-testid="no-delivery-agents-alert"
              >
                <AlertCircle size={16} color="#dc2626" />
                <span>
                  No active delivery persons available in CRM. Please add active Delivery personnel in <b>Users & Roles</b> before sending orders to Delivery.
                </span>
              </div>
            ) : (
              <div>
                <label className="dispatch-label" style={{ fontSize: 12, marginBottom: 4 }}>
                  Select Delivery Person <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <select
                  className="form-control"
                  value={selectedAgentId}
                  onChange={(e) => setSelectedAgentId(e.target.value)}
                  data-testid="direct-delivery-agent-select"
                  style={{
                    borderColor: formErrors.deliveryAgent ? "#ef4444" : undefined,
                  }}
                >
                  <option value="">-- Select Delivery Person --</option>
                  {deliveryAgents.map((a) => (
                    <option key={a.user_id || a.name} value={a.user_id || a.name}>
                      {a.name} {a.phone ? `(${a.phone})` : ""} — Delivery Agent
                    </option>
                  ))}
                </select>
                {formErrors.deliveryAgent && (
                  <span style={{ color: "#dc2626", fontSize: 11, marginTop: 2, display: "block" }}>
                    {formErrors.deliveryAgent}
                  </span>
                )}

                {/* Selected Agent Live Info Card */}
                {selectedAgent && (
                  <div
                    style={{
                      marginTop: 10,
                      padding: "10px 14px",
                      background: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      borderRadius: 6,
                      fontSize: 12,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600, color: "#0f172a" }}>
                        👤 {selectedAgent.name}
                      </div>
                      <div style={{ color: "#475569", fontSize: 11.5, marginTop: 2 }}>
                        Department: Delivery · {selectedAgent.email || "Pinaki Logistics"}
                      </div>
                    </div>
                    {selectedAgent.phone && (
                      <div style={{ color: "#2563eb", fontWeight: 600, fontSize: 12 }}>
                        📞 {selectedAgent.phone}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ borderTop: "1px solid var(--line)" }} />

          {/* SECTION 4: Instructions & Notes */}
          <div>
            <label className="dispatch-label" style={{ fontSize: 12, marginBottom: 4 }}>
              Delivery Instructions / Remarks (Optional)
            </label>
            <textarea
              className="form-control"
              rows={2}
              value={deliveryRemarks}
              onChange={(e) => setDeliveryRemarks(e.target.value)}
              placeholder="e.g. Call client 30 mins before arrival. Direct delivery bypassing production."
              data-testid="direct-delivery-remarks"
              style={{ resize: "vertical" }}
            />
          </div>

          {/* Modal Footer / Actions */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 12,
              marginTop: 10,
              paddingTop: 16,
              borderTop: "1px solid var(--line)",
            }}
          >
            <button
              type="button"
              className="outline-btn"
              onClick={onClose}
              disabled={submitting}
              data-testid="direct-delivery-cancel-btn"
              style={{ padding: "8px 16px", fontSize: 13 }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="primary-btn"
              disabled={submitting || (deliveryAgents.length === 0 && !loadingAgents)}
              data-testid="direct-delivery-submit-btn"
              style={{
                background: "#7c3aed",
                borderColor: "#7c3aed",
                padding: "8px 20px",
                fontSize: 13,
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                opacity: (deliveryAgents.length === 0 && !loadingAgents) ? 0.6 : 1,
                cursor: (deliveryAgents.length === 0 && !loadingAgents) ? "not-allowed" : "pointer",
              }}
            >
              <Truck size={15} />
              {submitting
                ? "Sending to Delivery…"
                : isBulk
                ? `Confirm & Send ${orders.length} Orders to Delivery`
                : "Confirm & Send to Delivery"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
